import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Sortable } from "@itandi/itandi-bb-ui/Sortable";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Toggle } from "@itandi/itandi-bb-ui/Toggle";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import { useCallback, useState } from "react";
import useSWRMutation from "swr/mutation";
import { renderDropDownButton } from "~/components/common/parts/dropdown/DropDownButton";
import { VendorUserModal } from "~/components/specifics/vendors/settings/vendor_users/VendorUserModal";
import { useIsItandi } from "~/context/VendorSession";
import { useVendorUsers } from "~/hooks/vendors/useVendorUsers";
import type { VendorUser } from "~/models/VendorUser";
import { isSuccess } from "~/utils/api/api_handler";
import { updateEnabled, updateSeq } from "~/utils/api/vendors/VendorUser";
import { generatePath } from "~/utils/generatePath";

type SortableItems = { data: VendorUser; id: string }[];

export function VendorUserList() {
	const { notify } = useNotification();
	const canEditSlackMemberId = useIsItandi();
	const { data: vendorUsers, mutate } = useVendorUsers().index();
	const [editingVendorUser, setEditingVendorUser] = useState<VendorUser | null>(
		null
	);

	const { trigger: seqTrigger } = useSWRMutation(
		generatePath("/vendors/vendor_users"),
		async (_key, { arg }: { arg: { sortedItems: VendorUser[] } }) =>
			updateSeq(arg.sortedItems)
	);

	const { trigger: enabledTrigger, isMutating: isUpdatingEnabled } =
		useSWRMutation(
			generatePath("/vendors/vendor_users"),
			async (
				_key,
				{ arg }: { arg: { enabled: boolean; vendorUserId: number } }
			) => updateEnabled(arg.vendorUserId, arg.enabled)
		);

	const sortableItems: SortableItems = (vendorUsers ?? []).map(
		(vendorUser) => ({
			data: vendorUser,
			id: String(vendorUser.id)
		})
	);

	const onSortEnd = useCallback(
		async (newItems: SortableItems) => {
			const sortedItems: VendorUser[] = newItems.map((item, index) => ({
				...item.data,
				seq: index + 1
			}));
			const result = await seqTrigger(
				{ sortedItems },
				{ optimisticData: sortedItems, rollbackOnError: true }
			);
			if (!isSuccess(result)) {
				notify({ content: "並び順の更新に失敗しました。", variant: "error" });
			}
		},
		[notify, seqTrigger]
	);

	const handleUpdateEnabled = async (
		vendorUser: VendorUser,
		enabled: boolean
	): Promise<void> => {
		const result = await enabledTrigger({
			enabled,
			vendorUserId: vendorUser.id
		});
		if (isSuccess(result)) {
			notify({
				content: enabled ? "「有効」にしました。" : "「無効」にしました。",
				variant: "success"
			});
			await mutate();
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	};

	return (
		<>
			<Table>
				<Thead>
					<Tr>
						<Th width="3rem">並替</Th>
						<Th width="4rem">有効</Th>
						<Th>担当者名</Th>
						<Th>メールアドレス</Th>
						<Th>電話番号</Th>
						{canEditSlackMemberId ? <Th>Slack ID</Th> : null}
						<Th width="6rem">操作</Th>
					</Tr>
				</Thead>
				<Tbody>
					<Sortable
						items={sortableItems}
						onSortEnd={onSortEnd}
						variant="vertical"
					>
						{({ dragHandle, item }) => {
							const background = !item.data.enabled ? "disabled" : undefined;
							return (
								<Tr key={item.id}>
									<Td background={background}>
										<Flex justifyContent="center">{dragHandle}</Flex>
									</Td>
									<Td background={background}>
										<Toggle
											checked={item.data.enabled}
											disabled={isUpdatingEnabled}
											onChange={(e) =>
												handleUpdateEnabled(item.data, e.target.checked)
											}
										/>
									</Td>
									<Td background={background}>{item.data.name}</Td>
									<Td background={background}>{item.data.email}</Td>
									<Td background={background}>{item.data.tel}</Td>
									{canEditSlackMemberId ? (
										<Td background={background}>
											{item.data.slackMemberId ?? "未設定"}
										</Td>
									) : null}
									<Td background={background}>
										<Dropdown button={renderDropDownButton("操作")}>
											<DropdownMenu
												disabled={!item.data.enabled}
												onClick={() => {
													setEditingVendorUser(item.data);
												}}
											>
												編集
											</DropdownMenu>
										</Dropdown>
									</Td>
								</Tr>
							);
						}}
					</Sortable>
				</Tbody>
			</Table>
			{editingVendorUser ? (
				<VendorUserModal
					onClose={() => {
						setEditingVendorUser(null);
					}}
					vendorUser={editingVendorUser}
				/>
			) : null}
		</>
	);
}
